import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import {
  Pencil,
  Calendar,
  MapPin,
  User,
  Users,
  Megaphone,
  Shield,
  AlertTriangle,
  Sparkles,
  LogOut,
  Mail,
  UserCircle2,
  CalendarClock,
  History as HistoryIcon,
  ChevronRight,
  Compass,
} from "lucide-react";
import { OnboardingReplayButton } from "@/components/OnboardingTour";

const toneLabel = {
  folksy: "Folksy",
  professional: "Professional",
  fiery: "Fiery",
  measured: "Measured",
};

function Field({ label, value, icon: Icon, multiline = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className={`mt-1 text-slate-800 ${multiline ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
        {value || <span className="text-slate-400 italic">Not provided</span>}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/campaign")
      .then(({ data }) => setC(data || null))
      .catch(() => setC(null))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isCommittee = c?.account_type === "committee";
  const sectionTitle = isCommittee ? "Committee profile" : "Campaign profile";
  const editLabel = isCommittee ? "Edit committee" : "Edit campaign";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Settings</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Account &amp; profile</h1>
        <p className="text-slate-600 mt-2">Your account and the profile every draft is grounded in.</p>
      </div>

      {/* Account */}
      <section className="mt-8 bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8" data-testid="settings-account-card">
        <h2 className="font-display text-xl font-bold text-flag-navy">Account</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-5">
          <Field label="Name" icon={UserCircle2} value={user?.name} testid="settings-account-name" />
          <Field label="Email" icon={Mail} value={user?.email} testid="settings-account-email" />
        </div>
        <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="settings-logout-btn"
            className="h-10 px-4 border-2 font-semibold text-slate-700 hover:text-flag-red"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
          <OnboardingReplayButton
            testid="settings-replay-tour-btn"
            className="inline-flex items-center justify-center h-10 px-4 rounded-md border-2 border-slate-300 text-sm font-semibold text-slate-700 hover:text-flag-navy hover:border-flag-navy transition-colors"
          >
            <Compass className="h-4 w-4 mr-2" /> Take the welcome tour again
          </OnboardingReplayButton>
        </div>
      </section>

      {/* Campaign / Committee profile */}
      <section className="mt-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl font-bold text-flag-navy">{sectionTitle}</h2>
              {c && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="settings-account-type-badge">
                  {isCommittee ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                  {isCommittee ? "Party Committee" : "Candidate"}
                </span>
              )}
            </div>
            <p className="text-slate-600 mt-1 text-sm">This profile feeds every generated post.</p>
          </div>
          {c && (
            <Link to="/intake" data-testid="settings-edit-campaign-btn">
              <Button variant="outline" className="h-10 px-4 border-2">
                <Pencil className="h-4 w-4 mr-2" /> {editLabel}
              </Button>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="mt-6 text-slate-500">Loading…</div>
        ) : !c ? (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
            <p className="text-slate-700 font-semibold">You haven't set up your profile yet.</p>
            <p className="text-slate-500 mt-1">Run the intake to enable post generation.</p>
            <Link to="/intake" data-testid="settings-start-intake-btn">
              <Button className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold">
                Start intake
              </Button>
            </Link>
          </div>
        ) : isCommittee ? (
          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="settings-basics-card">
              <Field label="Committee" icon={Users} value={c.committee_name} testid="settings-committee-name-field" />
              <Field label="Party" icon={Megaphone} value={c.party} testid="settings-party-field" />
              <Field label="Jurisdiction" icon={MapPin} value={c.jurisdiction} testid="settings-jurisdiction-field" />
              <Field label="Voice / tone" icon={Sparkles} value={toneLabel[c.tone] || c.tone} testid="settings-tone-field" />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="settings-platform-card">
              <Field label="Committee background" value={c.bio} multiline testid="settings-bio-field" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Countywide priorities</div>
                <ul className="mt-2 space-y-2" data-testid="settings-issues-list">
                  {(c.top_issues || []).length > 0 ? (
                    c.top_issues.map((t, i) => (
                      <li key={`issue-${i}-${t}`} className="flex items-start gap-2 text-slate-800">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-flag-red flex-shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-slate-400 italic">Not provided</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5 sm:col-span-2" data-testid="settings-events-card">
              <Field label="Recurring committee events" icon={CalendarClock} value={c.key_events} multiline testid="settings-key-events-field" />
              <Field label="Opposing party's messaging" icon={Shield} value={c.opposition_messaging} multiline testid="settings-opposition-messaging-field" />
              <Field label="Vulnerabilities to avoid" icon={AlertTriangle} value={c.own_weaknesses} multiline testid="settings-own-weak-field" />
            </div>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="settings-basics-card">
              <Field label="Candidate" icon={User} value={c.candidate_name} testid="settings-candidate-field" />
              <Field label="Office sought" icon={Megaphone} value={c.office_sought} testid="settings-office-field" />
              <Field label="District / jurisdiction" icon={MapPin} value={c.district} testid="settings-district-field" />
              <Field label="Election date" icon={Calendar} value={c.election_date} testid="settings-date-field" />
              <Field label="Voice / tone" icon={Sparkles} value={toneLabel[c.tone] || c.tone} testid="settings-tone-field" />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="settings-platform-card">
              <Field label="Background / bio" value={c.bio} multiline testid="settings-bio-field" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top priorities</div>
                <ul className="mt-2 space-y-2" data-testid="settings-issues-list">
                  {(c.top_issues || []).length > 0 ? (
                    c.top_issues.map((t, i) => (
                      <li key={`top-${i}-${t}`} className="flex items-start gap-2 text-slate-800">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-flag-red flex-shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-slate-400 italic">Not provided</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5 sm:col-span-2" data-testid="settings-opposition-card">
              <Field label="Opponent" icon={Shield} value={c.opponent_name} testid="settings-opponent-field" />
              <Field label="Opponent's platform" value={c.opponent_platform} multiline testid="settings-opp-platform-field" />
              <Field label="Opponent's vulnerabilities" value={c.opponent_vulnerabilities} multiline testid="settings-opp-vuln-field" />
              <Field label="Your weaknesses to avoid" icon={AlertTriangle} value={c.own_weaknesses} multiline testid="settings-own-weak-field" />
            </div>
          </div>
        )}
      </section>

      {/* History */}
      <section className="mt-8" data-testid="settings-history-section">
        <div>
          <h2 className="font-display text-xl font-bold text-flag-navy">History</h2>
          <p className="text-slate-600 mt-1 text-sm">Review past drafts, refinements, and generated speeches.</p>
        </div>
        <Link
          to="/history"
          data-testid="settings-history-link"
          className="mt-4 group flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6 hover:border-flag-navy hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
              <HistoryIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="font-semibold text-slate-900">Open history</div>
              <div className="text-sm text-slate-500">Posts, refinements, and speeches in one place.</div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-flag-navy transition-colors" />
        </Link>
      </section>

      {/* Change password */}
      <section className="mt-8" data-testid="settings-security-section">
        <div>
          <h2 className="font-display text-xl font-bold text-flag-navy">Security</h2>
          <p className="text-slate-600 mt-1 text-sm">Update your password. We never store it in plaintext — only a one-way hash.</p>
        </div>
        <ChangePasswordCard />
      </section>
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk(false);
    if (newPw.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (newPw !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      setOk(true);
      setCurrentPw(""); setNewPw(""); setConfirm("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6 space-y-4 max-w-lg" data-testid="settings-change-password-card">
      <div>
        <Label htmlFor="cur-pw" className="text-sm font-semibold text-slate-800">Current password</Label>
        <PasswordInput id="cur-pw" autoComplete="current-password" required minLength={1} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} data-testid="settings-current-password-input" className="mt-1 h-11" />
      </div>
      <div>
        <Label htmlFor="new-pw" className="text-sm font-semibold text-slate-800">New password</Label>
        <PasswordInput id="new-pw" autoComplete="new-password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="settings-new-password-input" className="mt-1 h-11" placeholder="At least 6 characters" />
      </div>
      <div>
        <Label htmlFor="confirm-pw" className="text-sm font-semibold text-slate-800">Confirm new password</Label>
        <PasswordInput id="confirm-pw" autoComplete="new-password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="settings-confirm-password-input" className="mt-1 h-11" />
      </div>
      {error && <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="settings-change-password-error">{error}</div>}
      {ok && <div className="text-sm text-green-900 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="settings-change-password-success">Password updated.</div>}
      <Button type="submit" disabled={busy} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5" data-testid="settings-change-password-submit">
        {busy ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
}
